import { Button } from "primereact/button";
import { Message } from "primereact/message";
import { Tag } from "primereact/tag";
import { Fragment, ReactNode, useEffect, useState } from "react";
import {
  useGetBenefitComparisonClusterQuery,
  useRescoreBenefitChecksMutation,
} from "store/queries/benefitComparison";
import {
  ComparisonEngineTag,
  EngineDisagreement,
  IComparisonChecksRequest,
  IComparisonClusterDetail,
  IComparisonExample,
  WorklistState,
} from "types/BenefitComparison/BenefitComparison";

import { BenefitComparisonChecks } from "./BenefitComparisonChecks";
import {
  COMPARISON_BENEFIT_LABELS,
  formatComparisonValue,
  RERUN_BUTTON_LABEL,
  SOURCE_TAGS,
} from "./benefitComparisonWording";
import { EngineDisagreesWith, FLAG_STATES } from "./EngineDisagreesWith";
import { PverifyRequestLink } from "./RemitAccuracyLinks";
import { lineLabel, relativeTime } from "./remitAccuracyWording";

function Tags({
  engineTag,
  aiTag,
  equalsLegacy,
  equalsRemit,
}: {
  engineTag: ComparisonEngineTag;
  aiTag: boolean;
  equalsLegacy: boolean;
  equalsRemit: boolean;
}) {
  return (
    <div className="flex gap-2">
      {/* 🚨 The Remit tag first: it is the only one of the four that can say the engine was wrong. */}
      {equalsRemit && (
        <Tag
          {...SOURCE_TAGS.remit}
          value="Remit"
          data-testid="benefitComparison.cluster.remitTag"
        />
      )}
      {engineTag && (
        <Tag
          {...SOURCE_TAGS.engine}
          value={engineTag === "READ" ? "Engine" : "Engine value"}
        />
      )}
      {aiTag && (
        <Tag
          {...SOURCE_TAGS.ai}
          value="AI"
          data-testid="benefitComparison.cluster.aiTag"
        />
      )}
      {equalsLegacy && <Tag {...SOURCE_TAGS.legacy} value="Legacy" />}
    </div>
  );
}

/**
 * The cluster's counts, as the mockups put them: one small table beside the response, read down.
 *
 * 🚨 "No remit or verdict · legacy or AI disagrees" is `checks − confirmed`, and it is the whole
 * point of the panel: it separates the checks something can settle from the ones where legacy or
 * the AI merely disagree, which are a lead rather than a miss.
 */
function ThisCluster({
  detail,
}: {
  detail: IComparisonClusterDetail | undefined;
}) {
  const cluster = detail?.cluster;
  const rows: [string, ReactNode][] = [
    ["Checks in this cluster", cluster?.checks ?? 0],
    ["Has a remit or human input", cluster?.confirmed ?? 0],
    [
      "Engine disagrees with it",
      <strong>{cluster?.engineDisagrees ?? 0}</strong>,
    ],
    ["Engine matches it", cluster?.engineMatches ?? 0],
    [
      "No remit or verdict · legacy or AI disagrees",
      Math.max((cluster?.checks ?? 0) - (cluster?.confirmed ?? 0), 0),
    ],
    [
      "Human input recorded",
      `${detail?.verdicts.recorded ?? 0} of ${
        detail?.verdicts.needingReview ?? 0
      } needed`,
    ],
    ["Remit / human input conflicts", cluster?.conflicts ?? 0],
  ];
  return (
    <div
      className="border-1 surface-border border-round p-3 h-full"
      data-testid="benefitComparison.cluster.counts"
    >
      <span className="block text-xs font-bold uppercase text-color-secondary mb-2">
        This cluster
      </span>
      <table className="w-full border-collapse">
        <tbody>
          {rows.map(([label, value]) => (
            <tr key={label} className="border-bottom-1 surface-border">
              <td className="p-2">{label}</td>
              <td className="p-2 text-right white-space-nowrap">{value}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Row({
  testId,
  location,
  message,
  placeOfService,
  benefitLevel,
  network,
  value,
  tags,
}: {
  testId: string;
  location: string;
  message: ReactNode;
  placeOfService: ReactNode;
  benefitLevel: ReactNode;
  network: string;
  value: ReactNode;
  tags: ReactNode;
}) {
  return (
    <tr data-testid={testId}>
      <td className="p-2 white-space-nowrap font-mono text-sm">{location}</td>
      <td className="p-2">{message}</td>
      <td className="p-2">{placeOfService}</td>
      <td className="p-2 white-space-nowrap">{benefitLevel}</td>
      <td className="p-2">{network}</td>
      <td className="p-2">{value}</td>
      <td className="p-2">{tags}</td>
    </tr>
  );
}

/**
 * The stored pVerify response for the SELECTED check, with every source tagged on the lines it
 * points at.
 *
 * 🚨 `Legacy` and `Remit` are VALUE-EQUALITY, not attribution: neither stores a location. The
 * engine's tag is attribution, and the AI's is its exact location.
 */
function ExampleResponse({
  example,
  request,
}: {
  example: IComparisonExample;
  request: IComparisonChecksRequest;
}) {
  const value = (amount: number | null) =>
    amount === null ? (
      <span className="text-color-secondary">no value</span>
    ) : (
      formatComparisonValue(amount, request.benefit)
    );
  const none = <span className="text-color-secondary">—</span>;
  const linesOf = (serviceIndex: number) =>
    example.lines.filter((line) => line.serviceIndex === serviceIndex);
  const { aiLocation } = example;
  let aiWhere = "nothing (none)";
  if (aiLocation?.type === "SUMMARY")
    aiWhere = `${aiLocation.block} › ${aiLocation.field}`;
  if (aiLocation?.type === "LINE")
    aiWhere = `ServiceDetails[${aiLocation.serviceIndex}] › EligibilityDetails[${aiLocation.lineIndex}]`;

  return (
    <>
      <div className="overflow-x-auto">
        <table
          className="w-full border-collapse"
          data-testid="benefitComparison.cluster.lines"
        >
          <thead>
            <tr className="text-left border-bottom-1 surface-border">
              <th className="p-2">Where in the response</th>
              <th className="p-2">Line message</th>
              <th className="p-2">Place of service</th>
              <th className="p-2">Benefit level</th>
              <th className="p-2">Net</th>
              <th className="p-2">Value</th>
              <th className="p-2" aria-label="Tags" />
            </tr>
          </thead>
          <tbody>
            {example.summaries.map((summary) => (
              <Row
                key={`${summary.block}.${summary.field}`}
                testId={`benefitComparison.cluster.summary.${summary.block}.${summary.field}`}
                location={`${summary.block} › ${summary.field}`}
                message={
                  summary.notes ?? (
                    <span className="text-color-secondary">no notes</span>
                  )
                }
                placeOfService={none}
                benefitLevel={none}
                network={summary.field.includes("OutNet") ? "Out" : "In"}
                value={value(summary.value)}
                tags={<Tags {...summary} />}
              />
            ))}
            {example.serviceLines.map((service) => (
              <Fragment key={service.serviceIndex}>
                <tr className="surface-100">
                  <td className="p-2 font-semibold" colSpan={7}>
                    {`${service.serviceName} (ServiceDetails[${service.serviceIndex}])`}
                  </td>
                </tr>
                {linesOf(service.serviceIndex).map((line) => (
                  <Row
                    key={line.lineIndex}
                    testId={`benefitComparison.cluster.line.${line.serviceIndex}.${line.lineIndex}`}
                    location={`EligibilityDetails[${line.lineIndex}]`}
                    message={lineLabel(line)}
                    placeOfService={line.placeOfService ?? none}
                    benefitLevel={
                      line.planNetworkId ? (
                        <span title={line.planNetworkName ?? undefined}>
                          {line.planNetworkId}
                        </span>
                      ) : (
                        none
                      )
                    }
                    network={line.planNetworkIndicator ?? "—"}
                    value={value(line.value)}
                    tags={<Tags {...line} />}
                  />
                ))}
              </Fragment>
            ))}
            {example.summaries.length === 0 && example.lines.length === 0 && (
              <tr>
                <td className="p-2" colSpan={7}>
                  No tagged lines in this response.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {!example.aiShown && example.aiValue !== null && (
        <p className="text-sm mt-2 mb-0">
          The AI read {aiWhere}, which is not among the rows above.
        </p>
      )}
      {example.engineValueLines > 1 && (
        <p className="text-sm mt-2 mb-0">
          The engine doesn&apos;t record which of these{" "}
          {example.engineValueLines} lines it read.
        </p>
      )}
    </>
  );
}

/**
 * CLE-2820 — ONE CLUSTER: the evidence header, the selected check's response with every source
 * tagged, and the checks themselves.
 *
 * 🚨 SELECTION IS BY CLICK AND NOTHING ELSE. Clicking anywhere in a check row loads it into the
 * response panel above and opens its verdict panel. There is no row expander and there is no
 * keyboard shortcut — no digit keys, no J/K, no global keydown handler.
 */
export function BenefitComparisonCluster({
  request,
  state,
  disagreesWith = [],
  onBack,
  actions,
}: {
  request: IComparisonChecksRequest;
  /**
   * 🚨 The State of the worklist row that was clicked, carried in the URL rather than recomputed:
   * it is that row's ranking, and deriving it a second time here would be a second implementation
   * of `rankWorklist` free to disagree with the list the user just came from. Absent on a
   * deep-linked URL, in which case the header simply has no tag.
   */
  state?: WorklistState;
  /**
   * 🚨 Which sources the engine differs from in this cluster, carried from the row for the same
   * reason the State is. The cluster detail counts disagreements by PATTERN and cannot separate a
   * remit from a human verdict, so re-deriving these here would let the header disagree with the
   * row that opened it. Empty on a deep-linked URL, in which case the header has no chips.
   */
  disagreesWith?: EngineDisagreement[];
  onBack: () => void;
  /**
   * The same "Hide cluster" cell the worklist row shows (tag, Undo, the button), rendered beside the
   * title so a reviewer can hide the cluster she is looking at without finding it again in the list.
   * Absent while the worklist row has not loaded, or when this cluster is not on the worklist.
   */
  actions?: ReactNode;
}) {
  const [selected, setSelected] = useState<string | null>(null);
  const requestKey = JSON.stringify(request);
  useEffect(() => setSelected(null), [requestKey]);

  const { data, isLoading, isFetching, isError } =
    useGetBenefitComparisonClusterQuery({
      ...request,
      ...(selected ? { examplePverifyResponseId: selected } : {}),
    });
  const [rescore, { isLoading: isRescoring }] =
    useRescoreBenefitChecksMutation();
  const detail = data?.data;
  const cluster = detail?.cluster;

  if (isError)
    return <Message severity="error" text="The cluster could not be loaded." />;

  const runAgain = () =>
    rescore({
      accountId: request.accountId as string,
      payerId: request.payerId,
      benefit: request.benefit,
      readFrom: request.readFrom,
      ...(request.planNameMissing
        ? { planNameMissing: true }
        : { planName: request.planName }),
    });

  return (
    <div data-testid="benefitComparison.cluster">
      <Button
        type="button"
        icon="pi pi-angle-left"
        label="Engine worklist"
        className="p-button-text p-button-sm px-0"
        onClick={onBack}
      />
      {/*
       * 🚨 THE HEADER IS THE TITLE AND THE STATE TAG, and nothing else — as the mockups have it.
       * It used to carry a one-sentence evidence verdict, the engine's read location, the AI's
       * all-time remit accuracy with its ASC caveat, and a verdict tally: four claims competing for
       * the same glance, none of them in the design. The counts now live in one place, the panel on
       * the right, where they can be read against one another.
       */}
      <div className="flex flex-wrap align-items-start justify-content-between gap-2 mt-3 mb-2">
        <h3 className="m-0">
          {cluster
            ? `${cluster.accountName} · ${cluster.payerName} · ${
                cluster.planName ?? "No plan name"
              } · ${COMPARISON_BENEFIT_LABELS[cluster.benefit]}`
            : "Loading…"}
        </h3>
        {actions && (
          <div data-testid="benefitComparison.cluster.dismissal">{actions}</div>
        )}
      </div>
      {/*
       * 🚨 THE SOURCE CHIPS, NOT THE STATE. This header carried the single State tag and was wrong
       * in exactly the way the worklist row was: a cluster has one state but up to four
       * disagreements, so a header reading "Remits disagree" on a cluster the engine also differs
       * from legacy and the AI on states the least of what is known about it. Same component as the
       * table, so the two can never drift apart again.
       */}
      {/*
       * 🚨 NOTHING AT ALL ON A DEEP LINK THAT CARRIES NO SOURCES. Rendering the label over a dash
       * would assert the engine agrees with everything on a cluster opened straight from a URL,
       * which is the one thing this screen is never looking at.
       */}
      {(disagreesWith.length > 0 || (state && FLAG_STATES.includes(state))) && (
        <div
          className="flex flex-wrap align-items-center gap-2 mb-3"
          data-testid="benefitComparison.cluster.state"
        >
          <span className="text-color-secondary">Engine disagrees with</span>
          <EngineDisagreesWith
            state={state}
            sources={disagreesWith}
            testId="benefitComparison.cluster.disagreesWith"
          />
        </div>
      )}

      <div className="grid mb-4">
        <div className="col-12 lg:col-7">
          <div className="border-1 surface-border border-round p-3 h-full">
            <div className="flex flex-wrap align-items-baseline justify-content-between gap-2 mb-3">
              <h4 className="m-0">What the response looks like</h4>
              {detail?.example && (
                <span className="text-color-secondary">
                  Showing RequestID{" "}
                  <PverifyRequestLink
                    requestId={detail.example.pverifyRequestId}
                  />
                </span>
              )}
            </div>
            {/*
             * 🚨 Selecting another check re-reads this panel and takes a second or two. Without a
             * sign of it the old response just sits there, so the panel reads as the check that was
             * clicked when it is still the previous one.
             */}
            <div className="relative">
              {isFetching && !isLoading && (
                <div
                  className="absolute w-full h-full flex align-items-center justify-content-center surface-overlay opacity-60 z-1"
                  data-testid="benefitComparison.cluster.exampleLoading"
                >
                  <i
                    className="pi pi-spin pi-spinner text-2xl"
                    aria-hidden="true"
                  />
                  <span className="sr-only">Loading this check’s response</span>
                </div>
              )}
              {detail?.example ? (
                <ExampleResponse example={detail.example} request={request} />
              ) : (
                !isLoading && <p className="m-0">No example response.</p>
              )}
            </div>
          </div>
        </div>
        <div className="col-12 lg:col-5">
          <ThisCluster detail={detail} />
        </div>
      </div>

      <div className="flex flex-wrap align-items-center gap-3 mb-2">
        <h4 className="m-0">Eligibility checks in this cluster</h4>
        <span className="ml-auto text-color-secondary">
          {detail?.lastRunAt
            ? `last run ${relativeTime(detail.lastRunAt)}`
            : "not run yet"}
        </span>
        <Button
          type="button"
          label={RERUN_BUTTON_LABEL}
          className="p-button-outlined p-button-sm"
          loading={isRescoring}
          data-testid="benefitComparison.cluster.rerun"
          onClick={runAgain}
        />
      </div>

      <BenefitComparisonChecks
        request={request}
        selected={selected}
        onSelect={setSelected}
      />
    </div>
  );
}
